import React from 'react';
import { useTimezone } from '@app/contexts/useTimezone';
import { ModelRowProps } from './types';

const TIME_SLOTS = ['00', '06', '12', '18'];

const ModelRow: React.FC<ModelRowProps> = ({
  modelId,
  name,
  isSelected,
  selectedRuns,
  disabled = false,
  currentDates,
  onSelectionChange,
  onModelToggle,
  latestRun,
}) => {
  const { timezone } = useTimezone();
  const isRunSelected = (date: string, time: string): boolean => {
    return selectedRuns.some((run) => run.date === date && run.time === time);
  };

  const isRunAvailable = (date: string, time: string): boolean => {
    if (!latestRun) return false;
    const runTime = new Date(`${date}T${time}:00:00Z`);
    const latestRunTime = new Date(latestRun);
    return runTime <= latestRunTime;
  };

  const handleCircleClick = (date: string, time: string) => {
    if (disabled || !isRunAvailable(date, time)) return;

    const isCurrentlySelected = isRunSelected(date, time);
    const newSelections = isCurrentlySelected
      ? selectedRuns.filter((run) => !(run.date === date && run.time === time))
      : [...selectedRuns, { date, time }];

    onSelectionChange(modelId, newSelections);

    // Auto-select model when any run is selected
    if (newSelections.length > 0 && !isSelected) {
      onModelToggle(modelId);
    }

    // Auto-deselect model when all runs are unselected
    if (newSelections.length === 0 && isSelected) {
      onModelToggle(modelId);
    }
  };

  const handleModelChipClick = () => {
    if (disabled) return;

    // If any runs are selected, clear all selections for this model
    if (selectedRuns.length > 0) {
      onSelectionChange(modelId, []);
      if (isSelected) {
        onModelToggle(modelId);
      }
      return;
    }

    const latestRunTime = latestRun ? new Date(latestRun) : null;

    // Find the latest available run within the displayed grid
    let latestAvailable: { dateString: string; time: string } | null = null;

    currentDates.forEach((date) => {
      const dateString = `${date.year}-${date.month.padStart(2, '0')}-${date.day.padStart(2, '0')}`;
      TIME_SLOTS.forEach((time) => {
        const runTime = new Date(`${dateString}T${time}:00:00Z`);
        const available = latestRunTime ? runTime <= latestRunTime : false;

        if (available) {
          if (!latestAvailable) {
            latestAvailable = { dateString, time };
          } else {
            const currentLatestTime = new Date(
              `${latestAvailable.dateString}T${latestAvailable.time}:00:00Z`,
            );
            if (runTime > currentLatestTime) {
              latestAvailable = { dateString, time };
            }
          }
        }
      });
    });

    if (latestAvailable) {
      const { dateString, time } = latestAvailable;
      // Replace selections with the latest run
      onSelectionChange(modelId, [{ date: dateString, time }]);
      if (!isSelected) {
        onModelToggle(modelId);
      }
    } else {
      // Fallback to toggling the model if no available run is visible
      onModelToggle(modelId);
    }
  };

  const renderTimeSlots = (dateIndex: number) => {
    const date = currentDates[dateIndex];
    if (!date) return null;

    const dateString = `${date.year}-${date.month.padStart(2, '0')}-${date.day.padStart(2, '0')}`;

    return TIME_SLOTS.map((time, timeIndex) => {
      const available = isRunAvailable(dateString, time);
      const selected = isRunSelected(dateString, time);

      return (
        <div key={timeIndex} className="w-[25%] flex justify-center items-center py-0.5">
          <div
            className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 border ${
              selected
                ? 'bg-pink-500 border-pink-500 hover:bg-pink-600 hover:border-pink-600 cursor-pointer'
                : available
                  ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer'
                  : 'bg-gray-300 dark:bg-gray-600 border-gray-300 dark:border-gray-600 cursor-not-allowed opacity-50'
            }`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              if (available) {
                handleCircleClick(dateString, time);
              }
            }}
            title={`${dateString} ${time}:00 ${timezone === 'UTC' ? 'UTC' : timezone.split('/').pop()} ${selected ? '(selected)' : available ? '(available)' : '(not available)'}`}
          />
        </div>
      );
    });
  };

  return (
    <div className={`${isSelected ? 'bg-pink-50 dark:bg-pink-900/10' : ''}`}>
      <div className="flex">
        {/* Model name section */}
        <div className="w-2/5 flex items-center py-1 px-1.5">
          <div className="flex items-center space-x-1.5 w-full min-w-0">
            {/* Model chip */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                handleModelChipClick();
              }}
              className={`text-xs font-medium px-1.5 py-0.5 rounded transition-all duration-200 flex-1 text-left min-w-0 ${
                isSelected
                  ? 'bg-pink-500 text-white hover:bg-pink-600 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <span className="truncate block">{name}</span>
            </button>
          </div>
        </div>

        {/* Time slots section */}
        <div className="w-3/5 flex">
          {/* First date */}
          <div className="w-1/2 flex border-r dark:border-gray-700">{renderTimeSlots(0)}</div>

          {/* Second date */}
          <div className="w-1/2 flex">{renderTimeSlots(1)}</div>
        </div>
      </div>
    </div>
  );
};

export default ModelRow;
